import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { WEBSITE_CONTACT_FIELDS, WEBSITE_CONTACT_MAX, WEBSITE_SOCIAL_FIELDS, type PlatformWebsiteDto, type WebsiteContact } from "@delta/shared";
import { useLang } from "../../i18n";
import { api } from "../../lib/api";
import { Icon } from "../../components/Icon";
import { useToast } from "../../components/Toast";
import { errText, fieldErrs, Title, when } from "./common";

// The marketing website's settings (migration 057), Super Admin only: the contact details it shows. Which packages it
// lists is chosen in the package editor ("Show on website"); this screen shows them read-only, like the demo and public
// signup, with links to their own screens.

const LABEL: Record<keyof WebsiteContact, string> = {
  companyEn: "pfWsCompanyEn", companyAr: "pfWsCompanyAr", phone: "pfWsPhone", whatsapp: "pfWsWhatsapp", email: "pfWsEmail",
  addressEn: "pfWsAddressEn", addressAr: "pfWsAddressAr", hoursEn: "pfWsHoursEn", hoursAr: "pfWsHoursAr",
  socialX: "wsSocialX", socialInstagram: "wsSocialInstagram", socialLinkedin: "wsSocialLinkedin", socialFacebook: "wsSocialFacebook",
  socialTiktok: "wsSocialTiktok", socialSnapchat: "wsSocialSnapchat", socialYoutube: "wsSocialYoutube"
};
const LTR = new Set<keyof WebsiteContact>(["phone", "whatsapp", "email", ...WEBSITE_SOCIAL_FIELDS]);
const MAIN = WEBSITE_CONTACT_FIELDS.filter((k) => !k.startsWith("social"));

export function WebsiteSettings() {
  const { t, lang, L } = useLang();
  const toast = useToast();
  const [s, setS] = useState<PlatformWebsiteDto | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => { api<PlatformWebsiteDto>("GET", "/platform/website").then(setS, (e) => setFailed(errText(t, e))); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (failed) return <><Title text={t("pfWsTitle")} /><div className="state-box error"><Icon name="alert" /><h3>{failed}</h3></div></>;
  if (!s) return <><Title text={t("pfWsTitle")} /><div className="state-box"><p>{t("pfLoading")}</p></div></>;
  const save = async () => {
    try {
      setS(await api("PUT", "/platform/website", { contact: s.contact }));
      setErrs({}); toast(t("pfWsSaved"));
    } catch (e) { setErrs(fieldErrs(t, e)); toast(errText(t, e)); }
  };
  const shown = s.packages.filter((p) => p.website && p.active && p.public);
  const field = (k: keyof WebsiteContact) => (
    <div key={k} className={`field ${errs[`contact.${k}`] ? "invalid" : ""}`}><label htmlFor={`ws-${k}`}>{t(LABEL[k])}</label>
      <input id={`ws-${k}`} data-testid={`ws-${k}`} className="control" dir={rtl(k)} maxLength={WEBSITE_CONTACT_MAX[k]} value={s.contact[k]}
        placeholder={k.startsWith("social") ? "https://" : undefined}
        type={k === "email" ? "email" : k.startsWith("social") ? "url" : LTR.has(k) ? "tel" : "text"} onChange={(e) => setS({ ...s, contact: { ...s.contact, [k]: e.target.value } })} />
      <div className="ferr">{errs[`contact.${k}`]}</div></div>
  );
  const rtl = (k: keyof WebsiteContact) => (k.endsWith("Ar") ? "rtl" : LTR.has(k) ? "ltr" : undefined);
  return (
    <>
      <Title text={t("pfWsTitle")} />
      <section className="card" data-testid="pf-website"><div className="pf-card-b">
        <p className="pf-small">{t("pfWsIntro")}</p>
        <b>{t("pfWsContact")}</b><span className="pf-small">{t("pfWsContactHint")}</span>
        <div className="ob-form">{MAIN.map(field)}</div>
        <b>{t("wsSocial")}</b><span className="pf-small">{t("wsSocialHint")}</span>
        <div className="ob-form">{WEBSITE_SOCIAL_FIELDS.map(field)}</div>
        <div className="pf-row"><button className="btn btn-primary" data-testid="ws-save" onClick={() => void save()}>{t("save")}</button>
          <span className="pf-small">{when(s.updatedAt, lang)}</span></div>
      </div></section>
      <section className="card" data-testid="pf-website-packages"><div className="pf-card-b">
        <b>{t("pfWsPackages")}</b><span className="pf-small">{t("wsPackagesFromEditor")}</span>
        {shown.length === 0 ? <p className="pf-small" data-testid="ws-pkg-none">{t("wsNoPackagesShown")}</p> : (
          <ul className="pf-small">{shown.map((p) => <li key={p.id} data-testid={`ws-pkg-${p.code}`}><b>{L(p)}</b> · {p.code}</li>)}</ul>
        )}
        <div><Link className="btn" to="/platform/plans" data-testid="ws-edit-packages">{t("wsEditPackages")}</Link></div>
      </div></section>
      <section className="card" data-testid="pf-website-state"><div className="pf-card-b">
        <b>{t("pfWsState")}</b>
        <span className="pill" data-testid="ws-demo-state">{t(s.demoEnabled ? "pfWsDemoOn" : "pfWsDemoOff")}</span> <Link to="/platform/demo">{t("pfNavDemo")}</Link>
        <span className="pill" data-testid="ws-signup-state">{t(s.signupMode === "closed" ? "pfWsSignupClosed" : "pfWsSignupOpen")}</span> <Link to="/platform/settings">{t("pfNavSettings")}</Link>
      </div></section>
    </>
  );
}
